import { Typography } from "@/components/atoms";
import { CopyIcon, MapPinnedIcon } from "@/components/atoms/icons";
import { AdhaUnitStatusBadge, OkDialog } from "@/components/molecules";
import { ImageCarousel } from "@/components/molecules/ImageCarousel";
import { Toast } from "@/components/molecules/Toast";
import WebAppLayout from "@/components/organisms/WebAppLayout";
import { useWebDocumentTitle } from "@/core/hooks/useWebDocumentTitle";
import { useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { fetchPropertyAssetsByName } from "@/core/services/contentfulClient";
import { resolveBrochureUrlsFromAssets } from "@/core/utils/brochureDownload";
import { trackDynatraceEvent } from "@/core/services/dynatraceService";
import { useAppStore } from "@/core/stores/appStore";
import { isSmallScreen } from "@/core/utils/responsive";
import { shareFile } from "@/core/wrappers/sharing";
import {
  calculatePropertyOutstanding,
  formatDueDateWithPrefix,
  getPropertyInstallments,
  mapInstallmentStatusToPaymentStatus,
} from "@/modules/payments/utils/installmentUtils";
import { DOCUMENT_TYPES } from "@/modules/profile/constants/documentTypes";
import { downloadAndPersistDocument } from "@/modules/property-details/utils/documentDownload";
import * as Clipboard from "expo-clipboard";
import * as Linking from "expo-linking";
import { router, useLocalSearchParams } from "expo-router";
import React, { useEffect, useMemo, useState } from "react";
import {
  ImageSourcePropType,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { AboutPropertyWeb } from "./components/AboutPropertyWeb";
import { PropertyDocumentsAccordionWeb } from "./components/PropertyDocumentsAccordionWeb";
import { PropertyOwnerDetailsAccordion } from "./components/PropertyOwnerDetailsAccordion";
import { PropertyPaymentsAccordionWeb } from "./components/PropertyPaymentsAccordionWeb";
import { checkFloorPlanAvailability } from "./utils/propertyDocuments";

export function PropertyDetailsScreenWeb() {
  const colors = useUIColors();   
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useI18nContext();
  const { propertyId } = useLocalSearchParams<{ propertyId?: string }>();
  const isMobile = isSmallScreen();

  // Dialog + toast state (reused from mobile)
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [floorPlanLoading, setFloorPlanLoading] = useState(false);
  const [floorPlanSuccessVisible, setFloorPlanSuccessVisible] = useState(false);
  const [floorPlanSuccessMessage, setFloorPlanSuccessMessage] = useState("");
  const [floorPlanSavedUri, setFloorPlanSavedUri] = useState<string | null>(
    null,
  );
  const [isFloorPlanAvailable, setIsFloorPlanAvailable] = useState(false);

  // Property images + Contentful brochure / name / description
  const [propertyImages, setPropertyImages] = useState<ImageSourcePropType[]>(
    [],
  );
  const [brochureUrls, setBrochureUrls] = useState<string[]>([]);
  const [contentfulProjectName, setContentfulProjectName] = useState<
    string | undefined
  >();
  const [contentfulDescription, setContentfulDescription] = useState<
    string | undefined
  >();

  // Store data
  const properties = useAppStore((state) => state.properties);
  const unitInstallments = useAppStore((state) => state.unitInstallments);
  const user = useAppStore((state) => state.auth.user);

  const property = useMemo(() => {
    if (propertyId) {
      return properties.find((p) => p.id === propertyId);
    }
    return properties[0];
  }, [propertyId, properties]);

  const installments = useMemo(() => {
    if (!property) return null;
    return getPropertyInstallments(unitInstallments, property.id);
  }, [property, unitInstallments]);

  const outstandingAmount = useMemo(() => {
    return calculatePropertyOutstanding(installments);
  }, [installments]);

  const installmentPaidPercent = useMemo(() => {
    if (!property) return 0;
    if (property.totalPrice > 0) {
      return Math.round((property.paidAmount / property.totalPrice) * 100);
    }
    return 0;
  }, [property]);

  useEffect(() => {
    const loadPropertyAssets = async () => {
      if (!property?.projectName) {
        setPropertyImages([]);
        setBrochureUrls([]);
        setContentfulProjectName(undefined);
        setContentfulDescription(undefined);
        return;
      }
      try {
        const assets = await fetchPropertyAssetsByName(property.projectName, {
          maxWidth: 1200,
          locale: currentLanguage === "ar" ? "ar" : "en",
        });
        const imageSources: ImageSourcePropType[] = assets.images.map(
          (img) => ({
            uri: img.url,
          }),
        );
        setPropertyImages(imageSources);
        setBrochureUrls(resolveBrochureUrlsFromAssets(assets));
        setContentfulProjectName(assets.name?.trim() || undefined);
        setContentfulDescription(assets.description?.trim() || undefined);
      } catch (error) {
        logger.error(
          "PropertyDetailsScreenWeb",
          "Failed to fetch property assets",
          {
            projectName: property.projectName,
            error,
          },
        );
        setPropertyImages([]);
        setBrochureUrls([]);
        setContentfulProjectName(undefined);
        setContentfulDescription(undefined);
      }
    };
    void loadPropertyAssets();
  }, [property?.projectName, currentLanguage]);

  const displayProjectName =
    contentfulProjectName?.trim() || property?.projectName || "";
  const propertyHeaderTitle =
    [property?.unitNumber, displayProjectName].filter(Boolean).join(", ") ||
    "Property";
  const projectDescription = contentfulDescription?.trim() || "";

  const ownerDetailsAccordion = useMemo(() => {
    if (!property?.buyerName?.trim()) {
      return null;
    }
    const buyerName = property.buyerName.trim();
    const jointOwners = property.jointOwners || [];
    const jointOwnersTotalShare = jointOwners.reduce(
      (sum, owner) => sum + owner.share,
      0,
    );
    const mainUserShare = Math.max(0, 100 - jointOwnersTotalShare);
    const owners = [
      {
        id: "main-owner",
        name: buyerName,
        share: mainUserShare,
        isPrimary: true,
      },
      ...jointOwners.map((owner, index) => ({
        id: `joint-owner-${index}`,
        name: owner.name,
        share: owner.share,
        isPrimary: false,
      })),
    ];
    return <PropertyOwnerDetailsAccordion owners={owners} />;
  }, [property]);

    // Track property details view
    useEffect(() => {
      if (property) {
        setIsFloorPlanAvailable(checkFloorPlanAvailability(property.documents as any));
        trackDynatraceEvent({
          p_screen: "Property Details",
          p_actionName: "Property Details Viewed Web",
          p_sectionName: "Property Details",
          eventType: "view",
          propertyId: property.id,
          projectName: property.projectName || "",
          unitNumber: property.unitNumber || "",
          buildingName: property.buildingName || "",
        });
      }
    }, [property]);

  const paymentItems = useMemo(() => {
    if (!installments) return [];

    const translatePaymentType = (type: string): string => {
      if (type === null || type === undefined) return "";
      if (typeof type !== "string") return type as any;
      const typeLower = type.toLowerCase();
      if (typeLower.includes("handover")) return t("propertyDetails.handover");
      if (typeLower.includes("installment"))
        return t("propertyDetails.installment");
      if (typeLower.includes("down payment"))
        return t("propertyDetails.downPayment");
      if (typeLower.includes("adm fee") || typeLower.includes("adm"))
        return t("propertyDetails.admFee");
      return type;
    };

    const allInstallments = [
      ...installments.overdue.map((item) => ({
        ...item,
        category: "overdue" as const,
      })),
      ...installments.upcomingDue.map((item) => ({
        ...item,
        category: "upcomingDue" as const,
      })),
      ...installments.future.map((item) => ({
        ...item,
        category: "future" as const,
      })),
      ...installments.paid.map((item) => ({
        ...item,
        category: "paid" as const,
      })),
    ];

    return allInstallments.map((item) => ({
      id: item.id,
      title: translatePaymentType(item.type || item.payment_name),
      amount:
        item.category === "paid"
          ? item.paid_amount || item.amount || 0
          : item.due_amount || 0,
      dueDate: formatDueDateWithPrefix(
        item.due_date,
        item.category === "paid"
          ? t("payments.dueDate")
          : t("propertyDetails.dueBy"),
      ),
      status: mapInstallmentStatusToPaymentStatus(item.category, item.status),
      type: (item.payment_type === "installment" ||
      item.payment_type === "other_charge"
        ? "property"
        : "community") as "property" | "community",
    }));
  }, [installments, t]);

  const documentTitle =
    propertyHeaderTitle !== "Property"
      ? propertyHeaderTitle
      : t("propertyDetails.title");
  useWebDocumentTitle(documentTitle);

  if (!property) {
    return (
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
      >
        <Typography variant="body" color="primary">
          {t("propertyDetails.noPropertyData")}
        </Typography>
      </View>
    );
  }

  const handleViewFloorPlan = async () => {
    try {
      if (!property?.id || !user?.customer_id) {
        throw new Error("Property or customer unavailable");
      }

      setFloorPlanLoading(true);

      const downloadResult = await downloadAndPersistDocument({
        docType: DOCUMENT_TYPES.FLOOR_PLAN as any,
        parentId: property.id,
        customerId: user.customer_id,
        translationKey: "propertyDetails.viewFloorPlan",
        t: t as any,
      });

      if (
        downloadResult.success &&
        downloadResult.fileUri &&
        downloadResult.message
      ) {
        setFloorPlanSuccessMessage(downloadResult.message);
        setFloorPlanSavedUri(downloadResult.fileUri);
        setFloorPlanSuccessVisible(true);
      } else {
        throw new Error(downloadResult.error || "Failed to save floor plan");
      }
    } catch (error: any) {
      logger.error("PropertyDetailsScreenWeb", "View floor plan failed", {
        error: error?.message || error,
      });
      setDialogMessage(error?.message || "Unable to open floor plan");
      setDialogVisible(true);
    } finally {
      setFloorPlanLoading(false);
    }
  };

  const handlePaySelected = (selectedPaymentIds: string[]) => {
    router.push(
      `/(auth)/property-payments?tab=upcoming&selectedPaymentIds=${selectedPaymentIds.join(",")}` as any,
    );
  };

  const handleViewAll = (tab: "upcoming" | "history") => {
    router.push(`/(auth)/property-payments?tab=${tab}` as any);
  };

  const handleMapPress = () => {
    if (!property.latitude || !property.longitude) {
      setDialogMessage(
        "Location coordinates are not available for this property.",
      );
      setDialogVisible(true);
      return;
    }

    const { latitude, longitude } = property;
    let mapUrl: string;

    if (Platform.OS === "ios") {
      mapUrl = `http://maps.apple.com/?q=${latitude},${longitude}`;
    } else if (Platform.OS === "android") {
      mapUrl = `geo:${latitude},${longitude}?q=${latitude},${longitude}`;
    } else {
      mapUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    }

    Linking.openURL(mapUrl).catch((err) => {
      logger.error("PropertyDetailsScreenWeb:Failed to open maps", err);
      setDialogMessage("Failed to open maps application.");
      setDialogVisible(true);
    });
  };

  const handleCopyAddressingNumber = async () => {
    if (!property.addressingNumber) {
      return;
    }

    try {
      await Clipboard.setStringAsync(property.addressingNumber);
      setToastMessage(t("propertyDetails.addressingNumberCopied"));
      setToastVisible(true);
    } catch (error) {
      logger.error(
        "PropertyDetailsScreenWeb:Failed to copy addressing number",
        error,
      );
      setDialogMessage(t("propertyDetails.failedToCopy"));
      setDialogVisible(true);
    }
  };

  return (
    <WebAppLayout
      activeTab="properties"
      headerTitle={t("propertyDetails.title")}
      breadcrumbs={[
        {
          label: t("properties.myProperties"),
          path: "/(auth)/(home)/properties",
        },
        { label: t("propertyDetails.title") },
      ]}
    >
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
      >
        {/* Main content */}
        <View style={styles.mainContent}>
          <ScrollView
            style={styles.contentArea}
            contentContainerStyle={styles.contentScroll}
          >
            {isMobile ? (
              /* Mobile: Single column layout (same as mobile version) */
              <>
                {/* Image carousel */}
                <ImageCarousel images={propertyImages} height={230} showNavigationButtons />

                {/* Property title & location */}
                <View style={styles.propertyHeader}>
                  <Pressable style={styles.addressRow} onPress={handleMapPress}>
                    <Typography variant="title3" color="primary">
                      {propertyHeaderTitle}
                    </Typography>
                    <MapPinnedIcon size={24} color={colors.fontPrimary} />
                  </Pressable>

                  {projectDescription ? (
                    <Typography
                      variant="body"
                      color="secondary"
                      style={[
                        styles.projectDescription,
                        isRTL ? styles.spaAvailableRTL : undefined,
                      ]}
                    >
                      {projectDescription}
                    </Typography>
                  ) : null}

                  {property.addressingNumber && (
                    <Pressable
                      style={styles.addressingRow}
                      onPress={handleCopyAddressingNumber}
                    >
                      <Typography variant="body" color="primary">
                        {t("propertyDetails.addressingNumber")}{" "}
                      </Typography>
                      <Typography variant="headline" color="primary">
                        {property.addressingNumber}
                      </Typography>
                      <CopyIcon size={16} color={colors.fontPrimary} />
                    </Pressable>
                  )}

                  <Typography
                    variant="body"
                    color="secondary"
                    style={isRTL ? styles.spaAvailableRTL : undefined}
                  >
                    {t("propertyDetails.spaAvailable")}
                  </Typography>
                  {property.isADHAUnit ? (
                    <AdhaUnitStatusBadge rawStatus={property.adhaUnitStatus} />
                  ) : null}
                </View>

                {/* Payment summary card */}
                <View
                  style={[
                    styles.summaryCard,
                    { backgroundColor: colors.backgroundLevel1 },
                  ]}
                >
                  <View
                    style={[
                      styles.progressBackground,
                      { width: `${installmentPaidPercent}%` },
                    ]}
                  />
                  <View
                    style={[styles.summaryLeft, isRTL && styles.summaryLeftRTL]}
                  >
                    <View style={styles.summaryLeftContent}>
                      <Typography
                        variant="caption"
                        color="secondary"
                        style={isRTL ? styles.summaryTextRTL : undefined}
                      >
                        {t("propertyDetails.paymentCompleted")}
                      </Typography>
                      <Typography
                        variant="headline"
                        color="primary"
                        style={isRTL ? styles.summaryTextRTL : undefined}
                      >
                        {installmentPaidPercent}%
                      </Typography>
                    </View>
                  </View>
                  <View
                    style={[
                      styles.summaryRight,
                      isRTL && styles.summaryRightRTL,
                    ]}
                  >
                    <Typography variant="caption" color="secondary">
                      {t("propertyDetails.amountOutstanding")}
                    </Typography>
                    <Typography variant="headline" color="primary">
                      {outstandingAmount.toLocaleString()}
                    </Typography>
                  </View>
                </View>

                {/* About property, documents, owner details */}
                <AboutPropertyWeb
                  bedrooms={property.bedrooms || 0}
                  bathrooms={property.bathrooms || 0}
                  areaSqFt={property.area || 0}
                  amenities={property.amenities || []}
                  onViewFloorPlan={handleViewFloorPlan}
                  floorPlanLoading={floorPlanLoading}
                  isFloorPlanAvailable={isFloorPlanAvailable}
                />

                <PropertyDocumentsAccordionWeb
                  salesOrderId={property.salesOrderId}
                  unitId={property.id}
                  documents={property.documents as any}
                  unitLabel={[property.unitNumber, displayProjectName]
                    .filter(Boolean)
                    .join(" ")}
                  brochureUrls={brochureUrls}
                  projectName={displayProjectName || property.projectName}
                />

                {ownerDetailsAccordion}

                {/* Payments accordion */}
                <PropertyPaymentsAccordionWeb
                  payments={paymentItems as any}
                  onlinePaymentEnabled={property.isOnlinePaymentEnabled === true}
                  onPaySelected={handlePaySelected}
                  onViewAll={handleViewAll}
                />
              </>
            ) : (
              /* Desktop: Two column layout */
              <View style={styles.contentRow}>
                {/* Left column */}
                <View style={styles.leftColumn}>
                  {/* Image carousel */}
                  <ImageCarousel images={propertyImages} height={421} showNavigationButtons />

                  {/* Property title & location */}
                  <View style={styles.propertyHeader}>
                    <Pressable
                      style={styles.addressRow}
                      onPress={handleMapPress}
                    >
                      <Typography variant="title3" color="primary">
                        {propertyHeaderTitle}
                      </Typography>
                      <MapPinnedIcon size={24} color={colors.fontPrimary} />
                    </Pressable>

                    {projectDescription ? (
                      <Typography
                        variant="body"
                        color="secondary"
                        style={[
                          styles.projectDescription,
                          isRTL ? styles.spaAvailableRTL : undefined,
                        ]}
                      >
                        {projectDescription}
                      </Typography>
                    ) : null}

                    {property.addressingNumber && (
                      <Pressable
                        style={styles.addressingRow}
                        onPress={handleCopyAddressingNumber}
                      >
                        <Typography variant="body" color="primary">
                          {t("propertyDetails.addressingNumber")}{" "}
                        </Typography>
                        <Typography variant="headline" color="primary">
                          {property.addressingNumber}
                        </Typography>
                        <CopyIcon size={16} color={colors.fontPrimary} />
                      </Pressable>
                    )}

                    <Typography
                      variant="body"
                      color="secondary"
                      style={isRTL ? styles.spaAvailableRTL : undefined}
                    >
                      {t("propertyDetails.spaAvailable")}
                    </Typography>
                    {property.isADHAUnit ? (
                      <AdhaUnitStatusBadge rawStatus={property.adhaUnitStatus} />
                    ) : null}
                  </View>

                  {/* About property, documents, owner details */}
                  <AboutPropertyWeb
                    bedrooms={property.bedrooms || 0}
                    bathrooms={property.bathrooms || 0}
                    areaSqFt={property.area || 0}
                    amenities={property.amenities || []}
                    onViewFloorPlan={handleViewFloorPlan}
                    floorPlanLoading={floorPlanLoading}
                    isFloorPlanAvailable={isFloorPlanAvailable}
                  />

                  <PropertyDocumentsAccordionWeb
                    salesOrderId={property.salesOrderId}
                    unitId={property.id}
                    documents={property.documents as any}
                    unitLabel={[property.unitNumber, displayProjectName]
                      .filter(Boolean)
                      .join(" ")}
                    brochureUrls={brochureUrls}
                    projectName={displayProjectName || property.projectName}
                  />

                  {ownerDetailsAccordion}
                </View>

                {/* Right column - payments */}
                <View style={styles.rightColumn}>
                  {/* Payment summary strip */}
                  <View
                    style={[
                      styles.summaryCard,
                      { backgroundColor: colors.backgroundLevel1 },
                    ]}
                  >
                    <View
                      style={[
                        styles.progressBackground,
                        { width: `${installmentPaidPercent}%` },
                      ]}
                    />
                    <View
                      style={[
                        styles.summaryLeft,
                        isRTL && styles.summaryLeftRTL,
                      ]}
                    >
                      <View style={styles.summaryLeftContent}>
                        <Typography
                          variant="caption"
                          color="secondary"
                          style={isRTL ? styles.summaryTextRTL : undefined}
                        >
                          {t("propertyDetails.paymentCompleted")}
                        </Typography>
                        <Typography
                          variant="headline"
                          color="primary"
                          style={isRTL ? styles.summaryTextRTL : undefined}
                        >
                          {installmentPaidPercent}%
                        </Typography>
                      </View>
                    </View>
                    <View
                      style={[
                        styles.summaryRight,
                        isRTL && styles.summaryRightRTL,
                      ]}
                    >
                      <Typography variant="caption" color="secondary">
                        {t("propertyDetails.amountOutstanding")}
                      </Typography>
                      <Typography variant="headline" color="primary">
                        {outstandingAmount.toLocaleString()}
                      </Typography>
                    </View>
                  </View>

                  {/* Payments accordion */}
                  <PropertyPaymentsAccordionWeb
                    payments={paymentItems as any}
                    onlinePaymentEnabled={property.isOnlinePaymentEnabled === true}
                    onPaySelected={handlePaySelected}
                    onViewAll={handleViewAll}
                  />
                </View>
              </View>
            )}
          </ScrollView>
        </View>

        <OkDialog
          visible={dialogVisible}
          message={dialogMessage}
          onOk={() => setDialogVisible(false)}
        />
        <OkDialog
          visible={floorPlanSuccessVisible}
          title={t("common.downloadSuccessTitle")}
          message={floorPlanSuccessMessage}
          okText={
            Platform.OS === "ios" ? t("common.saveShare") : t("common.ok")
          }
          onOk={async () => {
            if (Platform.OS === "ios" && floorPlanSavedUri) {
              const shareResult = await shareFile({ uri: floorPlanSavedUri });
              if (!shareResult.success && shareResult.error) {
                logger.warn("PropertyDetailsScreenWeb", "Sharing failed", {
                  error: shareResult.error,
                  fileUri: floorPlanSavedUri,
                });
              }
            }
            setFloorPlanSuccessVisible(false);
            setFloorPlanSavedUri(null);
          }}
        />
        <Toast
          visible={toastVisible}
          message={toastMessage}
          onHide={() => setToastVisible(false)}
        />
      </View>
    </WebAppLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  mainContent: {
    flex: 1,
  },
  contentArea: {
    flex: 1,
  },
  contentScroll: {
    gap: 24,
    paddingBottom: 24,
  },
  contentRow: {
    flexDirection: "row",
    gap: 24,
    alignItems: "flex-start",
  },
  leftColumn: {
    flex: 3,
    gap: 16,
  },
  rightColumn: {
    flex: 2,
    gap: 16,
  },
  propertyHeader: {
    gap: 8,
  },
  projectDescription: {
    marginTop: 4,
  },
  addressRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingRight: 8,
  },
  addressingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  summaryCard: {
    flexDirection: "row",
    borderRadius: 12,
    overflow: "hidden",
    position: "relative",
  },
  progressBackground: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    backgroundColor: "#81c784",
    height: "100%",
  },
  summaryLeft: {
    width: 191,
    paddingHorizontal: 12,
    paddingVertical: 12,
    paddingRight: 8,
    position: "relative",
    flex: 1,
    alignItems: "flex-start",
    zIndex: 1,
  },
  summaryLeftRTL: {
    justifyContent: "flex-end",
  },
  summaryTextRTL: {
    textAlign: "right",
  },
  summaryLeftContent: {
    gap: 2,
  },
  summaryRight: {
    flex: 1,
    paddingHorizontal: 8,
    paddingVertical: 12,
    paddingLeft: 8,
    paddingRight: 12,
    gap: 2,
    alignItems: "flex-start",
    position: "relative",
    zIndex: 1,
  },
  // In RTL we want the contents of the outstanding amount capsule
  // to be visually right-aligned.
  summaryRightRTL: {
    justifyContent: "flex-end",
  },
  spaAvailableRTL: {
    textAlign: "right",
  },
});
